import React, { useEffect, useCallback, useRef } from 'react'

import classNames from 'classnames'
import ReactDOM from 'react-dom'

import Icon from './Icon'

export const CloseButton = ({ onClick, className = '' }) => (
  <button className={classNames(className)} onClick={onClick}>
    <Icon icon='cross' className='d-flex' />
  </button>
)

const ModalButtons = ({ buttons, buttonPlacement }) => {
  const autoFocusButtonRef = useRef(null)

  useEffect(() => {
    autoFocusButtonRef.current?.focus({ focusVisible: true })
  }, [])

  if (!buttons) return null

  return (
    <div className={classNames('btn-container', buttonPlacement)}>
      {buttons.map(({ type, title, onClick, disabled, autoFocus }, i) => (
        <button
          key={i}
          ref={autoFocus ? autoFocusButtonRef : null}
          disabled={disabled}
          className={classNames('btn', { [`btn-${type}`]: type })}
          onClick={onClick}
        >
          {title}
        </button>
      ))}
    </div>
  )
}

const Modal = ({
  children,
  className,
  isOpen,
  onClose,
  dataContains,
  header,
  title,
  buttons,
  buttonPlacement,
  closeOnBackdropClick = true,
  withDefaultModalContent = true,
  withDefaultPortal = false,
  ...others
}) => {
  const modalRef = useRef()

  const close = useCallback(
    e => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    },
    [isOpen, onClose]
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', close)
      return () => {
        document.removeEventListener('keydown', close)
      }
    }
  }, [isOpen, close])

  const onBackdropClick = event => {
    if (event.target === modalRef.current) onClose(event)
  }

  const suppressEventBubbling = e => {
    e.stopPropagation()
  }

  if (!isOpen) return null

  const modal = (
    <>
      <div className='modal-backdrop show' />
      <div
        className='modal d-block'
        ref={modalRef}
        data-contains={dataContains}
        onClick={closeOnBackdropClick ? onBackdropClick : null}
        {...others}
      >
        <div className={classNames('modal-dialog', className)} onClick={suppressEventBubbling}>
          {withDefaultModalContent && (
            <div className='modal-content'>
              <div className='modal-header align-items-center border-bottom-0'>
                {header || (title && <h2 className='modal-title'>{title}</h2>)}
                <CloseButton className='close' onClick={onClose} />
              </div>

              <div className='modal-body'>
                {children}
                <ModalButtons buttons={buttons} buttonPlacement={buttonPlacement} />
              </div>
            </div>
          )}
          {!withDefaultModalContent && <div className='modal-content'>{children}</div>}
        </div>
      </div>
    </>
  )

  if (withDefaultPortal) {
    return ReactDOM.createPortal(modal, document.querySelector('.modal-container'))
  } else {
    return modal
  }
}

export default Modal
